import React, { useState, useEffect } from 'react';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getDatabase, ref as dbRef, update, remove, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { Link } from 'react-router-dom';
import { compressImage, buildAvatarPath, AVATAR_CACHE_CONTROL } from '../utils/compressImage';
import './UserSettings.css';

const USE_CASE_LABELS = {
    email_automation: 'Email Automation',
    cold_outreach: 'Cold Outreach',
    crm: 'Customer Relationship Management',
    lead_generation: 'Lead Generation',
    newsletter: 'Newsletter & Updates',
    sales_follow_up: 'Sales Follow-Up',
    customer_onboarding: 'Customer Onboarding',
    event_marketing: 'Event Marketing',
};

function UserSettings() {
    const [userPhoto, setUserPhoto] = useState('');
    const [name, setName] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [useCases, setUseCases] = useState([]);
    const [setupDate, setSetupDate] = useState('');
    const [isPremium, setIsPremium] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [saveConfirmation, setSaveConfirmation] = useState(false);
    const [activeTab, setActiveTab] = useState('personal');

    const auth = getAuth();
    const currentUser = auth.currentUser;

    useEffect(() => {
        if (!currentUser) return;
        const db = getDatabase();
        get(dbRef(db, `Users/${currentUser.uid}`)).then((snapshot) => {
            if (!snapshot.exists()) return;
            const data = snapshot.val();
            setUserPhoto(data.userPhoto || '');
            setName(data.name || '');
            setFirstName(data.firstName || '');
            setLastName(data.lastName || '');
            setCompanyName(data.companyName || '');
            setEmail(data.email || currentUser.email || '');
            setUseCases(data.useCases || []);
            setSetupDate(data.signupTimestamp || data.setupDate || '');
            setIsPremium(data.isPremium || false);
        }).catch((err) => console.error('Error fetching user data:', err));
    }, [currentUser]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
        });
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);

        // Shrink before upload: a raw phone photo is megabytes that every viewer
        // re-downloads to fill a 96 px circle. Falls back to the original file if
        // the image cannot be re-encoded, so an upload never gets blocked.
        const image = await compressImage(file);
        const storage = getStorage();
        const fileRef = storageRef(storage, buildAvatarPath(currentUser.uid, image.extension));
        const task = uploadBytesResumable(fileRef, image.blob, {
            contentType: image.contentType,
            // Safe because buildAvatarPath gives every upload its own URL.
            cacheControl: AVATAR_CACHE_CONTROL,
        });
        task.on('state_changed', null,
            (err) => { console.error(err); setIsUploading(false); },
            () => {
                getDownloadURL(task.snapshot.ref).then((url) => {
                    setUserPhoto(url);
                    const db = getDatabase();
                    update(dbRef(db, `/Users/${currentUser.uid}`), { userPhoto: url })
                        .finally(() => setIsUploading(false));
                });
            }
        );
    };

    const handleSave = () => {
        if (!currentUser) return;
        const fullName = `${firstName} ${lastName}`.trim();
        const db = getDatabase();
        update(dbRef(db, `/Users/${currentUser.uid}`), {
            firstName, lastName, name: fullName, companyName, userPhoto,
        }).then(() => {
            setName(fullName);
            setSaveConfirmation(true);
            setTimeout(() => setSaveConfirmation(false), 3000);
        }).catch((err) => console.error('Error saving:', err));
    };

    const handleDeleteAccount = () => {
        if (!window.confirm('Are you sure you want to delete your account? This cannot be undone.')) return;
        const db = getDatabase();
        remove(dbRef(db, `/Users/${currentUser.uid}`))
            .then(() => console.log('Account deleted'))
            .catch((err) => console.error('Error deleting:', err));
    };

    return (
        <div className="us-page">
            <div className="us-container">
                {/* Top bar */}
                <div className="us-topbar">
                    <Link to="/" className="us-back">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Back to home
                    </Link>
                    {activeTab === 'personal' && (
                        <button
                            type="button"
                            className={`us-save-btn ${saveConfirmation ? 'us-save-btn--saved' : ''}`}
                            onClick={handleSave}
                        >
                            {saveConfirmation ? 'Saved!' : 'Save Settings'}
                        </button>
                    )}
                </div>

                <div className="us-heading">
                    <h1>User Settings</h1>
                    <p>Profile, plan, and account details</p>
                </div>

                <div className="us-tabs" role="tablist" aria-label="Settings sections">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'personal'}
                        className={`us-tab ${activeTab === 'personal' ? 'us-tab--active' : ''}`}
                        onClick={() => setActiveTab('personal')}
                    >
                        <i className="fa fa-user" />
                        Personal
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'danger'}
                        className={`us-tab ${activeTab === 'danger' ? 'us-tab--active' : ''}`}
                        onClick={() => setActiveTab('danger')}
                    >
                        <i className="fa fa-exclamation-triangle" />
                        Danger Zone
                    </button>
                </div>

                {activeTab === 'personal' && (
                    <>
                        {/* Profile card */}
                        <div className="us-card">
                            <div className="us-profile-header">
                                <label className="us-avatar-wrap" htmlFor="us-photo-upload">
                                    <input
                                        id="us-photo-upload"
                                        type="file"
                                        accept="image/*"
                                        onChange={handlePhotoUpload}
                                        style={{ display: 'none' }}
                                    />
                                    {isUploading ? (
                                        <div className="us-avatar-spinner" />
                                    ) : userPhoto ? (
                                        <>
                                            <img className="us-avatar-img" src={userPhoto} alt="Profile" />
                                            <div className="us-avatar-overlay">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                                                    <circle cx="12" cy="13" r="4" />
                                                </svg>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="us-avatar-placeholder">Add</div>
                                    )}
                                    <span className="us-avatar-plan">
                                        {isPremium ? 'PRO' : 'STARTER'}
                                        <img src={isPremium ? '/img/globus.svg' : '/img/salesgod_light.webp'} alt="" />
                                    </span>
                                </label>
                                <div className="us-profile-info">
                                    <h2>{name || 'Your Name'}</h2>
                                    <p className="us-profile-company">{companyName || 'Your Company'}</p>
                                    <p className="us-profile-email">{email}</p>
                                    <div className="us-member-badge">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                        Member since {formatDate(setupDate)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Edit profile */}
                        <div className="us-card">
                            <h3 className="us-section-title">Edit Profile</h3>
                            <div className="us-form-row">
                                <div className="us-field">
                                    <label htmlFor="us-fname">First Name</label>
                                    <input id="us-fname" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" />
                                </div>
                                <div className="us-field">
                                    <label htmlFor="us-lname">Last Name</label>
                                    <input id="us-lname" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" />
                                </div>
                            </div>
                            <div className="us-form-row us-form-row--full">
                                <div className="us-field">
                                    <label htmlFor="us-company">Company Name</label>
                                    <input id="us-company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Your Company Inc." />
                                </div>
                            </div>
                        </div>

                        {/* Use cases */}
                        {useCases.length > 0 && (
                            <div className="us-card">
                                <h3 className="us-section-title">Your Use Cases</h3>
                                <div className="us-tags">
                                    {useCases.map((uc, i) => (
                                        <span key={i} className="us-tag">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            {USE_CASE_LABELS[uc] || uc}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Account info */}
                        <div className="us-card">
                            <h3 className="us-section-title">Account Information</h3>
                            <div className="us-info-grid">
                                <div className="us-info-item">
                                    <span className="us-info-label">Email Address</span>
                                    <span className="us-info-value">{email}</span>
                                </div>
                                <div className="us-info-item">
                                    <span className="us-info-label">Account Created</span>
                                    <span className="us-info-value">{formatDate(setupDate)}</span>
                                </div>
                                <div className="us-info-item">
                                    <span className="us-info-label">Plan</span>
                                    <span className="us-info-value">
                                        <span className={`us-plan-badge ${isPremium ? 'us-plan-badge--pro' : 'us-plan-badge--starter'}`}>
                                            {isPremium ? 'PRO' : 'STARTER'}
                                            <img src={isPremium ? '/img/globus.svg' : '/img/salesgod_light.webp'} alt="" />
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'danger' && (
                    <div className="us-danger-card">
                        <div className="us-danger-warning">
                            <i className="fa fa-exclamation-triangle" />
                            <p>Changes here are permanent. Deleting your account cannot be reversed.</p>
                        </div>
                        <h3 className="us-danger-title">Delete Account</h3>
                        <p className="us-danger-text">
                            This will permanently delete your account and all associated data. This action cannot be undone.
                        </p>
                        <button type="button" className="us-danger-btn" onClick={handleDeleteAccount}>
                            Delete My Account
                        </button>
                    </div>
                )}

                <div className="us-legal-links">
                    <Link to="/impressum">Impressum</Link>
                    <Link to="/datenschutz">Datenschutz</Link>
                    <Link to="/agb">AGB</Link>
                    <Link to="/avv">AVV</Link>
                </div>
            </div>
        </div>
    );
}

export default UserSettings;
